import { Component, OnInit, OnDestroy } from '@angular/core'
import { collection, getDocs, query, where, doc, getDoc } from '@angular/fire/firestore'
import { Subscription as RxSubscription } from 'rxjs'
import { EnterpriseContextService } from '../../../services/enterprise-context.service'
import { AdminService } from '../../../services/admin.service'
import { Subscription, subscriptionsPath, ContractStatus, AdminClient, FirestoreStructure } from '@parkupp/core'

interface SubscriptionSummary {
    parkingTitle: string
    merchantName: string
    status: ContractStatus
    isActive: boolean
}

interface ParkerInfo {
    clientKey: string
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    companyName?: string
    subscriptionCount: number
    activeSubscriptionCount: number
    subscriptions: SubscriptionSummary[]
    // Convenience getters for display
    currentParkings: string[]
    currentPortfolios: string[]
}

@Component({
    selector: 'app-admin-parkers-page',
    templateUrl: './admin-parkers-page.component.html',
    styleUrls: ['./admin-parkers-page.component.scss'],
})
export class AdminParkersPageComponent implements OnInit, OnDestroy {
    private rxSubscriptions: RxSubscription[] = []
    
    parkers: ParkerInfo[] = []
    filteredParkers: ParkerInfo[] = []
    loading = false
    searchTerm = ''
    
    // Stats
    totalParkers = 0
    activeParkers = 0
    
    // Sorting
    currentSortColumn = 'activeSubscriptionCount'
    currentSortDirection: 'asc' | 'desc' = 'desc'
    
    // Active statuses for counting
    private activeStatuses = [
        ContractStatus.new,
        ContractStatus.paymentInitiated,
        ContractStatus.active,
        ContractStatus.bouncing,
        ContractStatus.renewing,
        ContractStatus.pendingClientApproval,
    ]

    constructor(
        public enterpriseContext: EnterpriseContextService,
        private adminService: AdminService
    ) {}

    ngOnInit(): void {
        // Subscribe to enterprise context changes
        const entSub = this.enterpriseContext.currentEnterpriseKey$.subscribe(() => {
            this.loadParkers()
        })
        this.rxSubscriptions.push(entSub)
        
        // Initial load
        this.loadParkers()
    }

    ngOnDestroy(): void {
        this.rxSubscriptions.forEach(sub => sub.unsubscribe())
    }

    async loadParkers(): Promise<void> {
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        
        if (!enterpriseKey) {
            this.parkers = []
            this.filteredParkers = []
            this.totalParkers = 0
            this.activeParkers = 0
            return
        }
        
        this.loading = true
        
        try {
            // Step 1: Query subscriptions for this enterprise to get client refs
            const subsQuery = query(
                collection(this.adminService.firestore, subscriptionsPath()),
                where('denormEnterpriseKey', '==', enterpriseKey)
            )
            
            const subsSnapshot = await getDocs(subsQuery)
            
            // Group subscriptions by client
            const clientSubscriptionsMap = new Map<string, Subscription[]>()
            
            subsSnapshot.docs.forEach(docSnap => {
                const sub = docSnap.data() as Subscription
                sub.$key = docSnap.id
                
                if (sub.clientRef) {
                    const clientKey = sub.clientRef.id
                    
                    if (!clientSubscriptionsMap.has(clientKey)) {
                        clientSubscriptionsMap.set(clientKey, [])
                    }
                    clientSubscriptionsMap.get(clientKey)!.push(sub)
                }
            })
            
            // Step 2: Fetch AdminClient data for each unique client
            const parkersList: ParkerInfo[] = []
            
            for (const [clientKey, subscriptions] of clientSubscriptionsMap) {
                // Build subscription summaries
                const subscriptionSummaries: SubscriptionSummary[] = subscriptions.map(sub => {
                    const isActive = this.activeStatuses.includes(sub.status as ContractStatus)
                    return {
                        parkingTitle: sub.denormParkingTitle || sub.manualParkingName || 'Unknown',
                        merchantName: sub.denormMerchantCompanyName || 'Unknown',
                        status: sub.status,
                        isActive
                    }
                })
                
                const activeCount = subscriptionSummaries.filter(s => s.isActive).length
                
                // Get unique active parking names and portfolios
                const activeSubs = subscriptionSummaries.filter(s => s.isActive)
                const currentParkings = [...new Set(activeSubs.map(s => s.parkingTitle))]
                const currentPortfolios = [...new Set(activeSubs.map(s => s.merchantName))]
                
                // Try to load AdminClient data (enterprise managers now have read access)
                let adminClient: AdminClient | null = null
                try {
                    const adminClientDocRef = doc(this.adminService.firestore, FirestoreStructure.ADMIN_CLIENTS, clientKey)
                    const adminClientSnap = await getDoc(adminClientDocRef)
                    if (adminClientSnap.exists()) {
                        adminClient = new AdminClient(adminClientSnap.data(), clientKey)
                    }
                } catch (e) {
                    console.warn(`Could not load AdminClient for ${clientKey}:`, e)
                }
                
                // Get fallback name from subscription if AdminClient doesn't exist
                const firstSub = subscriptions[0]
                const fallbackFirstName = firstSub?.denormClientFirstName || firstSub?.manualClientName || 'Unknown'
                const fallbackLastName = firstSub?.denormClientLastName || ''
                
                parkersList.push({
                    clientKey,
                    firstName: adminClient?.firstName || fallbackFirstName,
                    lastName: adminClient?.lastName || fallbackLastName,
                    email: adminClient?.email || '',
                    phoneNumber: adminClient?.phoneNumber || '',
                    companyName: adminClient?.companyName,
                    subscriptionCount: subscriptions.length,
                    activeSubscriptionCount: activeCount,
                    subscriptions: subscriptionSummaries,
                    currentParkings,
                    currentPortfolios,
                })
            }
            
            this.parkers = parkersList
            
            // Calculate stats
            this.totalParkers = this.parkers.length
            this.activeParkers = this.parkers.filter(p => p.activeSubscriptionCount > 0).length
            
            // Apply current sort and filter
            this.applyFilter()
            this.applySort()
            
        } catch (error) {
            console.error('[Parkers] Error loading parkers:', error)
        } finally {
            this.loading = false
        }
    }

    onSearchChange(): void {
        this.applyFilter()
    }

    private applyFilter(): void {
        if (!this.searchTerm.trim()) {
            this.filteredParkers = [...this.parkers]
        } else {
            const term = this.searchTerm.toLowerCase()
            this.filteredParkers = this.parkers.filter(p =>
                p.firstName.toLowerCase().includes(term) ||
                p.lastName.toLowerCase().includes(term) ||
                p.email.toLowerCase().includes(term) ||
                p.phoneNumber.includes(term) ||
                p.companyName?.toLowerCase().includes(term) ||
                p.currentParkings.some(parking => parking.toLowerCase().includes(term)) ||
                p.currentPortfolios.some(portfolio => portfolio.toLowerCase().includes(term))
            )
        }
        this.applySort()
    }

    sortBy(column: string): void {
        if (this.currentSortColumn === column) {
            this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc'
        } else {
            this.currentSortColumn = column
            this.currentSortDirection = 'desc'
        }
        this.applySort()
    }

    private applySort(): void {
        this.filteredParkers.sort((a, b) => {
            let valA: any = (a as any)[this.currentSortColumn]
            let valB: any = (b as any)[this.currentSortColumn]
            
            // Handle string comparison
            if (typeof valA === 'string') valA = valA.toLowerCase()
            if (typeof valB === 'string') valB = valB.toLowerCase()
            
            // Handle null/undefined
            if (valA == null) valA = ''
            if (valB == null) valB = ''
            
            let comparison = 0
            if (valA < valB) comparison = -1
            if (valA > valB) comparison = 1
            
            return this.currentSortDirection === 'asc' ? comparison : -comparison
        })
    }

    getSortIcon(column: string): string {
        if (this.currentSortColumn !== column) return 'fa-sort'
        return this.currentSortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
    }
}

